"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.documentFromLogs = exports.mergeLogs = exports.wrapDidDocument = exports.fetchDataFromEvents = exports.query = void 0;
const ethers_1 = require("ethers");
const did_resolver_interface_1 = require("@ew-did-registry/did-resolver-interface");
const constants_1 = require("../constants");
const utils_1 = require("../utils");
/**
 * This function updates the document if the event type is 'DelegateChange'
 *
 * @param event
 * @param did
 * @param document
 * @param validTo
 * @param block
 */
const handleDelegateChange = (event, did, document, validTo, block) => {
    var _a;
    const stringDelegateType = ethers_1.ethers.utils.parseBytes32String(event.values.delegateType);
    const publicKeyID = `${did}#delegate-${stringDelegateType}-${event.values.delegate}`;
    const publicKeyBlock = (_a = document.publicKey[publicKeyID]) === null || _a === void 0 ? void 0 : _a.block;
    if (document.publicKey[publicKeyID] === undefined ||
        (publicKeyBlock !== undefined && publicKeyBlock < block)) {
        switch (stringDelegateType) {
            case 'sigAuth':
                document.authentication[publicKeyID] = {
                    type: 'sigAuth',
                    publicKey: publicKeyID,
                    validity: validTo,
                    block,
                };
            // eslint-disable-next-line no-fallthrough
            case 'veriKey':
                document.publicKey[publicKeyID] = {
                    id: publicKeyID,
                    type: 'Secp256k1VerificationKey2018',
                    controller: did,
                    ethereumAddress: event.values.delegate,
                    validity: validTo,
                    block,
                };
                break;
            default:
                break;
        }
    }
    return document;
};
/**
 * This function updates the document on Attribute change event
 *
 * @param event
 * @param did
 * @param document
 * @param validTo
 * @param block
 */
const handleAttributeChange = (event, did, document, validTo, block) => {
    var _a, _b, _c;
    const matchDID = (0, utils_1.matchDIDPattern)(did);
    const identity = matchDID[1];
    const attributeType = event.values.name;
    const stringAttributeType = ethers_1.ethers.utils.parseBytes32String(attributeType);
    const match = stringAttributeType.match(constants_1.attributeNamePattern);
    if (match) {
        const section = match[1];
        const algo = match[2];
        const type = match[4];
        const encoding = match[6];
        if (section === 'pub') {
            let publicKeysPayload;
            try {
                const parsed = JSON.parse(Buffer.from(event.values.value.slice(2), 'hex').toString());
                if (typeof parsed === 'object') {
                    publicKeysPayload = parsed;
                }
                else {
                    return document;
                }
            }
            catch (e) {
                return document;
            }
            const pk = {
                id: `${did}#${publicKeysPayload.tag}`,
                type: `${algo}${type}`,
                controller: identity,
                validity: validTo,
                block,
            };
            const publicKeyBlock = (_a = document.publicKey[pk.id]) === null || _a === void 0 ? void 0 : _a.block;
            if (document.publicKey[pk.id] === undefined ||
                (publicKeyBlock !== undefined && publicKeyBlock < block)) {
                switch (encoding) {
                    case null:
                    case undefined:
                    case 'hex':
                        pk.publicKeyHex = publicKeysPayload.publicKey;
                        break;
                    case 'base64':
                        pk.publicKeyBase64 = Buffer.from(event.values.value.slice(2), 'hex').toString('base64');
                        break;
                    case 'pem':
                        pk.publicKeyPem = Buffer.from(event.values.value.slice(2), 'hex').toString();
                        break;
                    default:
                        break;
                }
                document.publicKey[pk.id] = pk;
            }
            return document;
        }
        if (section === 'svc') {
            const servicePoint = JSON.parse(Buffer.from(event.values.value.slice(2), 'hex').toString());
            servicePoint.validity = validTo;
            servicePoint.block = block;
            const serviceEndpointBlock = (_b = document.service[servicePoint.id]) === null || _b === void 0 ? void 0 : _b.block;
            if (document.service[servicePoint.id] === undefined ||
                (serviceEndpointBlock !== undefined && serviceEndpointBlock < block)) {
                document.service[servicePoint.id] = servicePoint;
            }
            return document;
        }
        return document;
    }
    const attrBlock = (_c = document.attributes.get(stringAttributeType)) === null || _c === void 0 ? void 0 : _c.block;
    if (!attrBlock || attrBlock < block) {
        const attributeData = {
            attribute: Buffer.from(event.values.value.slice(2), 'hex').toString(),
            validity: validTo,
            block,
        };
        document.attributes.set(stringAttributeType, attributeData);
    }
    return document;
};
/**
 * Update document checks the event validity, and, if valid,
 * passes the event parsing to the handler
 *
 * @param event
 * @param eventName
 * @param did
 * @param document
 * @param block
 */
const updateDocument = (event, did, document, block) => {
    const { validTo } = event.values;
    if (validTo) {
        switch (event.name) {
            case did_resolver_interface_1.DidEventNames.AttributeChanged:
                return handleAttributeChange(event, did, document, validTo, block);
            case did_resolver_interface_1.DidEventNames.DelegateChanged:
                return handleDelegateChange(event, did, document, validTo, block);
            default:
                return document;
        }
    }
    return document;
};
/**
 * Given a certain block from the chain, this function returns the events
 * associated with the did within the block
 *
 * @param block
 * @param did
 * @param document
 * @param provider
 * @param contractInterface
 * @param address
 */
const getEventsFromBlock = (block, did, document, provider, contractInterface, address) => new Promise((resolve, reject) => {
    const identity = (0, utils_1.addressOf)(did);
    provider
        .getLogs({
        address,
        fromBlock: block.toNumber(),
        toBlock: block.toNumber(),
        topics: [
            null,
            `0x000000000000000000000000${identity.slice(2).toLowerCase()}`,
        ],
    })
        .then((log) => {
        const { name, args, signature, topic } = contractInterface.parseLog(log[0]);
        const event = {
            name,
            values: args,
            signature,
            topic,
        };
        updateDocument(event, did, document, block.toNumber());
        resolve(event.values.previousChange);
    })
        .catch((error) => {
        reject(error);
    });
});
const query = (document, selector) => {
    const attrName = Object.keys(selector)[0];
    const attributes = Object.values(document[attrName]);
    if (attributes.length === 0) {
        return undefined;
    }
    const filter = Object.entries(selector[attrName]);
    return attributes.find((a) => filter.every(([prop, val]) => a[prop] && a[prop] === val));
};
exports.query = query;
/**
 * A high level function that manages the flow to read data from the blockchain
 *
 * @param did
 * @param document
 * @param registrySettings
 * @param contract
 * @param provider
 */
const fetchDataFromEvents = (did, document, registrySettings, contract, provider, selector) => __awaiter(void 0, void 0, void 0, function* () {
    const identity = (0, utils_1.addressOf)(did);
    let nextBlock;
    let topBlock;
    try {
        nextBlock = yield contract.changed(identity);
        topBlock = nextBlock;
    }
    catch (error) {
        throw new Error('Blockchain address did not interact with smart contract');
    }
    if (nextBlock) {
        document.owner = yield contract.owners(identity);
    }
    else if (identity) {
        document.owner = identity;
    }
    const contractInterface = new ethers_1.ethers.utils.Interface(JSON.stringify(registrySettings.abi));
    const { address } = registrySettings;
    while (nextBlock.toNumber() !== 0 &&
        nextBlock.toNumber() >= document.topBlock.toNumber()) {
        // eslint-disable-next-line no-await-in-loop
        nextBlock = yield getEventsFromBlock(nextBlock, did, document, provider, contractInterface, address);
        if (selector) {
            const attribute = (0, exports.query)(document, selector);
            if (attribute) {
                return;
            }
        }
    }
    document.topBlock = topBlock;
});
exports.fetchDataFromEvents = fetchDataFromEvents;
/**
 * Makes a copy of a log event and remove the validity and block
 * The log is used to construct DID Document,
 * but we don't want to include validity and block in DID Document.
 * It ,akes a copy of the logs so as to not remove from the original log
 * (as that log maybe need those properties elsewhere)
 * @param log log event from ERC1056
 * @returns copy of log without validity and block
 */
const copyAndRemoveValidityAndBlock = (log) => {
    const copy = Object.assign({}, log);
    if (log.block) {
        delete copy.block;
    }
    if (log.validity) {
        delete copy.validity;
    }
    return copy;
};
/**
 * Provided with the fetched data, the function parses it and returns the
 * DID Document associated with the relevant user
 *
 * @param did
 * @param document
 * @param context
 */
const wrapDidDocument = (did, document, context = 'https://www.w3.org/ns/did/v1') => {
    var _a, _b, _c;
    const now = ethers_1.BigNumber.from(Math.floor(new Date().getTime() / 1000));
    const publicKey = [];
    const authentication = [
        {
            type: 'owner',
            publicKey: `${did}#owner`,
            // -1 is preventing BigNumber.from overflow error https://github.com/ethers-io/ethers.js/discussions/1582
            validity: ethers_1.BigNumber.from(Number.MAX_SAFE_INTEGER - 1),
        },
    ];
    const didDocument = {
        '@context': context,
        id: did,
        publicKey,
        authentication,
        service: [],
    };
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const key in document.publicKey) {
        const pubKey = document.publicKey[key];
        const pubKeyValidity = (_a = pubKey.validity) === null || _a === void 0 ? void 0 : _a.gt(now);
        if (pubKeyValidity) {
            const pubKeyCopy = copyAndRemoveValidityAndBlock(pubKey);
            didDocument.publicKey.push(pubKeyCopy);
        }
    }
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const key in document.authentication) {
        const authenticator = document.authentication[key];
        const authenticatorValidity = (_b = authenticator.validity) === null || _b === void 0 ? void 0 : _b.gt(now);
        if (authenticatorValidity) {
            const authenticatorCopy = copyAndRemoveValidityAndBlock(authenticator);
            didDocument.authentication.push(authenticatorCopy);
        }
    }
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const key in document.service) {
        const serviceEndpoint = document.service[key];
        const serviceEndpointValidity = (_c = serviceEndpoint.validity) === null || _c === void 0 ? void 0 : _c.gt(now);
        if (serviceEndpointValidity) {
            const serviceEndpointCopy = copyAndRemoveValidityAndBlock(serviceEndpoint);
            didDocument.service.push(serviceEndpointCopy);
        }
    }
    return didDocument;
};
exports.wrapDidDocument = wrapDidDocument;
/**
 * Restore document from partially read logs
 *
 * @param logs {IDIDLogData[]}
 */
const mergeLogs = (logs) => {
    logs = logs.sort((a, b) => a.topBlock.sub(b.topBlock).toNumber());
    return logs.reduce((doc, log) => {
        doc.service = Object.assign(Object.assign({}, doc.service), log.service);
        doc.publicKey = Object.assign(Object.assign({}, doc.publicKey), log.publicKey);
        doc.authentication = Object.assign(Object.assign({}, doc.authentication), log.authentication);
        return doc;
    }, logs[0]);
};
exports.mergeLogs = mergeLogs;
const documentFromLogs = (did, logs) => {
    const mergedLogs = (0, exports.mergeLogs)(logs);
    return (0, exports.wrapDidDocument)(did, mergedLogs);
};
exports.documentFromLogs = documentFromLogs;
//# sourceMappingURL=functions.js.map